import React from "react";

import classnames from "classnames";
import { CartEmployeePriceBreakdownModal } from "EmployerApplication/Components/CartEmployeePriceBreakdown";
import ConfirmRemovePlanModal from "EmployerApplication/Components/ConfirmRemovePlanModal";
import { capitalize } from "lodash";
import { history } from "QuotePage/Store";
import { quotePageName, isRenewal } from "QuotePage/urls";
import { useDispatch, useSelector, connect } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { useIsEligibleForLifePlans } from "../shoppingCartSection/LifePlanEligibility";
import BodyText from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { TitleSmall } from "Cobalt/Header";
import { useModal } from "Cobalt/Modal";
import { PlanHeader, FeatureBox, MonthlyCost } from "Cobalt/Plan/Card";
import Stack from "Cobalt/Stack";
import { Card, CardHeader, CardSection } from "Common/Card/";
import { removeUpgradeOption, addPlanToCart } from "Common/CartService";
import { Div } from "Common/HTML";
import LifeCostBreakdownModal from "Common/LifeCostBreakdownModal";
import { MoreDetailsButton, MedicalPlanDetailsModal } from "Common/plans/MedicalPlanDetailsModal";
import PlanDetailsModal from "Common/plans/PlanDetailsModal";
import RemoveIcon from "Common/RemoveIcon";
import { SizeSwitch } from "Common/Responsive";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import type { MedicalPlanPrice } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("ShoppingCartPlanCardV2");

type RemovePlanButtonProps = {
  plan: MedicalPlanPrice;
  lastPlan: boolean;
  removeUpgradeOption: (arg0: MedicalPlanPrice) => unknown;
};

const RemovePlanButtonDumb = ({ plan, lastPlan, removeUpgradeOption, ...rest }: RemovePlanButtonProps) => {
  const dispatch = useDispatch();
  // TODO(life_plan) - subclasses
  const lifePlanInCart = useSelector((state) => state.cart?.life);
  const removingDentalPlan = plan.type === "dental";
  const removingVisionPlan = plan.type === "vision";
  const lifeEligibleAfterRemoval = useIsEligibleForLifePlans({
    removingDentalPlan,
    removingVisionPlan,
  });

  const { isShowing: confirmRemovePlanModalIsShowing, show: removePlanModalShow, hide: removePlanModalHide } = useModal();

  const handleRemovePlan = () => {
    AnalyticsTracking.trackEvent("plan_removed", {
      type: plan.type,
      deprecatedName: `remove-plan/${plan.type}`,
    });

    // If the plan is a medical plan, remove the upgrade option
    if (plan.type === "medical") {
      removeUpgradeOption(plan);

      if (lastPlan) {
        history.push(appendAuthParams(quotePageName()));
      }
      // When removing a dental or vision plan, we need to check if they
      // currently have a life plan, and if so, we need to check if they
      // will still be eligible for life insurance after removing the
      // dental or vision plan.
      // If they will no longer be eligible for life insurance, we
      // will launch a confirmation modal to confirm that they want to
      // remove the dental or vision plan.
      // Otherwise, we will simply remove the dental or vision plan
    } else if (lifePlanInCart && (plan.type === "dental" || plan.type === "vision")) {
      if (lifeEligibleAfterRemoval) {
        dispatch(addPlanToCart({ planType: plan.type, plan: null }));
      } else {
        removePlanModalShow();
      }
    } else {
      dispatch(addPlanToCart({ planType: plan.type, plan: null }));
    }
  };

  return (
    <>
      <ConfirmRemovePlanModal plan={plan} show={confirmRemovePlanModalIsShowing} onHide={removePlanModalHide} history={history} />

      <BodyText className={bem("RemovePlanButtonWrapper")}>
        <Button
          {...rest}
          p={0}
          className={bem("RemovePlanButton")}
          color="ghost"
          onClick={() => {
            handleRemovePlan();
          }}
        >
          <RemoveIcon />
        </Button>
      </BodyText>
    </>
  );
};

const mapDispatchToProps = { removeUpgradeOption };

export const RemovePlanButton = connect(() => ({}), mapDispatchToProps)(RemovePlanButtonDumb);

type ShoppingCartPlanCardProps = {
  plan: MedicalPlanPrice;
  itemTitle: string | null | undefined;
  lastPlan: boolean;
  isSampleQuote: boolean;
  showAverageCost: boolean;
  planType?: "medical" | "dental" | "vision" | "life";
};

const ShoppingCartPlanCard = (props: ShoppingCartPlanCardProps) => {
  const { plan, isSampleQuote, itemTitle, lastPlan, planType } = props;

  const renderSeeEmployeeCostsButton = () => {
    const params = {
      plan,
      triggerClassName: "see-employee-costs",
      triggerText: "View Cost Breakdown",
      trackOpen: () => {
        AnalyticsTracking.trackEvent("employee_costs_viewed", {
          location: "shopping_cart",
          plan_id: plan.id,
          name: plan.raw_name,
          brand: plan.carrier,
          ages_entered: true,
        });
      },
    };

    if (plan.type == "life") {
      return <LifeCostBreakdownModal type="life-cost-breakdown-modal" {...params} />;
    }

    return <CartEmployeePriceBreakdownModal {...params} />;
  };

  const renderMoreDetails = () => (
    <Div className={bem("Footer")}>
      <Div sm={6}>
        <Div>
          {plan.type === "life" && (
            <a href={plan.sbc_url} target="_blank" className={bem("DetailsLink")} rel="noreferrer">
              View Plan Details
            </a>
          )}
          {plan.target_market !== "mec" && plan.type !== "life" && <MoreDetailsButton plan={plan} showIcon={false} buttonTextOverride="View Plan Details" />}
        </Div>
        {plan.type === "medical" ? (
          <MedicalPlanDetailsModal plan={plan} isSampleQuote={isSampleQuote} inCart isAlternateCart />
        ) : (
          <PlanDetailsModal plan={plan} inCart />
        )}
      </Div>
    </Div>
  );

  const renderDesktopPlanCard = () => {
    return (
      <>
        {itemTitle && (
          <Div>
            <TitleSmall mt={0} mb={3}>
              {itemTitle}
            </TitleSmall>
          </Div>
        )}

        <Card p={0}>
          <CardSection>
            <Div mb={2}>
              <Stack direction="horizontal" justify="end" align="center">
                <RemovePlanButton plan={plan} lastPlan={lastPlan} />
              </Stack>
            </Div>
            <Div mb={3}>
              <Stack direction="horizontal" justify="space-between" align="center">
                <Stack direction="horizontal" gap="spacious" align="center">
                  <PlanHeader plan={plan} />
                  {(planType === "medical" || plan.type === "medical") && (
                    <>
                      <FeatureBox plan={plan} title="Tier" field="metal_tier" format="capitalize" />
                      <FeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />
                      <FeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
                      <FeatureBox plan={plan} title="Doctors Visits" field="estimated_costs.pcp_visit" format="currency" />
                    </>
                  )}
                  {(planType === "dental" || plan.type === "dental") && (
                    <>
                      <FeatureBox plan={plan} title="Max Benefit" field="annual_maximum" format="currency" />
                      <FeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
                      <FeatureBox plan={plan} title="Preventative" field="preventive_care" format="capitalize" />
                      <FeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />
                    </>
                  )}

                  {(planType === "vision" || plan.type === "vision") && (
                    <>
                      <FeatureBox plan={plan} title="Eye Exam" field="eye_exam" />
                      <FeatureBox plan={plan} title="Lenses" field="lenses" format="html" />
                      <FeatureBox plan={plan} title="Contacts" field="contacts" format="html" />
                    </>
                  )}

                  {(planType === "life" || plan.type === "life") && (
                    <>
                      <FeatureBox plan={plan} title="Coverage" field="coverage_amount" format="currency" />
                      <FeatureBox plan={plan} title="Policy Type" field="plan_type" />
                      <FeatureBox plan={plan} title="Contribution" value="100%" />
                    </>
                  )}
                </Stack>
                <Stack direction="horizontal" gap="condensed" align="center">
                  <MonthlyCost cost={plan.total_cost} loading={false} hasFootnote={isSampleQuote} costType="total" />
                </Stack>
              </Stack>
            </Div>
            <Stack direction="horizontal" justify="space-between" align="center">
              {renderMoreDetails()}
              {renderSeeEmployeeCostsButton()}
            </Stack>
          </CardSection>
        </Card>
      </>
    );
  };

  const renderMobilePlanCard = () => (
    <Card mb={planType === "medical" || plan.type === "medical" ? 0 : 4} p={0} className={bem("CardContainer")}>
      {" "}
      <CardHeader className={classnames(bem("Header"))} p={3}>
        <Div pl={2}>{capitalize(plan.type)}</Div>
        <RemovePlanButton plan={plan} lastPlan={lastPlan} />
      </CardHeader>
      <CardSection p={4} className={bem("Summary")}>
        <PlanHeader plan={plan} />
        <MonthlyCost cost={plan.total_cost} loading={false} hasFootnote={isSampleQuote} costType="total" />
      </CardSection>
      <CardSection p={4} className={bem("Summary", "Features")}>
        {(planType === "medical" || plan.type === "medical") && (
          <>
            <FeatureBox plan={plan} title="Tier" field="metal_tier" format="capitalize" />
            <FeatureBox plan={plan} title="Network Type" field="plan_type" format="acronym" />
            <FeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
          </>
        )}
        {(planType === "dental" || plan.type === "dental") && (
          <>
            <FeatureBox plan={plan} title="Max Benefit" field="annual_maximum" format="currency" />
            <FeatureBox plan={plan} title="Deductible" field="deductible" format="currency" />
            <FeatureBox plan={plan} title="Preventative" field="preventive_care" format="capitalize" />
          </>
        )}

        {(planType === "vision" || plan.type === "vision") && (
          <>
            <FeatureBox plan={plan} title="Eye Exam" field="eye_exam" />
            <FeatureBox plan={plan} title="Lenses" field="lenses" format="html" />
            <FeatureBox plan={plan} title="Contacts" field="contacts" format="html" />
          </>
        )}
        {(planType === "life" || plan.type === "life") && (
          <>
            <FeatureBox plan={plan} title="Coverage" field="coverage_amount" format="currency" />
            <FeatureBox plan={plan} title="Policy Type" field="plan_type" />
            <FeatureBox plan={plan} title="Contribution" value="100%" />
          </>
        )}
      </CardSection>
      <CardSection className={bem("Summary", "More")}>
        {renderMoreDetails()}
        {renderSeeEmployeeCostsButton()}
      </CardSection>
    </Card>
  );

  return <SizeSwitch xs={renderMobilePlanCard()} md={renderDesktopPlanCard()} />;
};

export default ShoppingCartPlanCard;
